import { faApple, faGoogle } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GooglePayButton from '@google-pay/button-react';
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Col, Row, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux';
import { clearCart, selectCartCoupon, selectCartItems, selectOrderInProgress, setOrderInProgress } from '../../features/cart/cartSlice';
import { selectShippingMethods } from '../../features/shipping/shippingSlice';
import ShippingMethodObj from '../../features/shipping/shippingMethodObj';
import { selectProducts } from '../../features/products/productsSlice';
import CartObj from '../../features/cart/cartObj';
import { FormValues } from '../checkout-form/interfaces';
import { selectToken } from '../../features/user/userSlice';
import AuthNetAPI from '../../API/authNetAPI';
import { selectGeneralOptions, selectIPAddress, selectIsMobileRoute, selectLifeTimeAffiliate, selectUtmParamsURL, selectUtmShipping, selectVisitorID } from '../../features/mobile/mobileSlice';
import { useStripe } from '@stripe/react-stripe-js';
import validateForm from './validateForm';
import DeliveryObj from '../../features/delivery/deliveryObj';
import { selectDeliveryData } from '../../features/delivery/deliverySlice';
import GooglePayAPI from '../../API/googlePayAPI';
import OrdersAPI from '../../API/ordersAPI';
import ProductObj from '../../features/products/productObj';
import { selectApplePayPayment, selectGooglePayPayment, selectPendingOrder, setApplePayPayment, setGooglePayPayment, setPendingOrder } from '../../features/checkout/checkoutSlice';
import CheckoutUtils from '../../features/checkout/checkoutUtils';
import OrderObj from '../../features/orders/orderObj';
import { resetCustomer, selectCustomer } from '../../features/customer/customerSlice';
import { setPointsCoupon } from '../../features/coupons/couponsSlice';
import { resetReferrals, selectAffiliate, selectVisit } from '../../features/referrals/referralsSlice';
import TiktokPixel from 'tiktok-pixel';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import { addOrder } from '../../features/orders/ordersSlice';
import CouponObj from '../../features/coupons/couponObj';
import CustomerObj from '../../features/customer/customerObj';
import { useNavigate } from 'react-router-dom';

interface Props {
    values: FormValues;
    totalValue: any;
    availableDelivery: boolean;
    setErrorMsg: (errorMsg: string) => void;
    isAddressVerified: boolean;
    setAddressResult: (addressResult: any) => void;
    setShowAddressVerification: (showAddressVerification: boolean) => void;
}

export default function PaymentOptions({ values, totalValue, availableDelivery, setErrorMsg, isAddressVerified, setAddressResult, setShowAddressVerification }: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = stripe && stripe.elements();
    let token = useSelector(selectToken);
    const pendingOrderData = useSelector(selectPendingOrder);
    const [canMakePayments, setCanMakePayments] = useState(false);
    const [isApplePayReady, setIsApplePayReady] = useState(false);
    const applePayResponse = useSelector(selectApplePayPayment);
    const googlePayResponse = useSelector(selectGooglePayPayment);
    const cartItems = useSelector(selectCartItems);
    const cart = useMemo(() => {
        return new CartObj(cartItems);
    }, [cartItems]);
    const [isFormValid, setIsFormValid] = useState(false);
    const products = useSelector(selectProducts);
    const ipAddress = useSelector(selectIPAddress);
    const orderInProgress = useSelector(selectOrderInProgress);
    const getGeneralOptions = useSelector(selectGeneralOptions);
    const shippingMethods = useSelector(selectShippingMethods);
    const deliveryInfo = new DeliveryObj(useSelector(selectDeliveryData));
    const cartCoupon = CouponObj.createCoupon(useSelector(selectCartCoupon));
    const custData = useSelector(selectCustomer)
    const customer = useMemo(() => {
        return new CustomerObj(custData);
    }, [custData]);
    const maxUsablePoints = customer.getMaxUsablePoints(
        cart.getSubtotal(null, null, null) as number
    );
    const affiliateData = useSelector(selectAffiliate);
    const referralVisitData = useSelector(selectVisit);
    const affiliateVisitorID = useSelector(selectVisitorID);
    const utmShipping = useSelector(selectUtmShipping);
    const AppURL = useSelector(selectIsMobileRoute);
    const utmParamsURL = useSelector(selectUtmParamsURL);
    const lifeTimeAffiliate = useSelector(selectLifeTimeAffiliate);
    const urlParams = AppURL ? `/?${AppURL}` : '';
    const totalPay = parseFloat(totalValue.replace('$', ''));
    const paymentOption = getGeneralOptions?.payment_option;
    const getPaymode = getGeneralOptions?.payment_mode;
    const isUtmShipping = (utmShipping && utmShipping.ship_price_value) ? true : false;
    const utmShippingValue = utmShipping && utmShipping.ship_price_value;
    const MPConversionRate = getGeneralOptions?.mp_redem_conversion_rate;
    const paymentMode = getPaymode === 'test' ? 'TEST' : 'PRODUCTION';
    const paymentGateway = paymentOption === 'stripe' ? paymentOption : 'authorizenet';
    const creditCardProcessor = paymentOption ? paymentOption : process.env.REACT_APP_CREDIT_CARD_PROCESSOR;
    const publishKey = getPaymode === 'test' ?
        process.env.REACT_APP_STAGE_STRIPE_PUBLISHABLE_KEY :
        process.env.REACT_APP_PROD_STRIPE_PUBLISHABLE_KEY;
    const authMerchantId = getPaymode === 'test' ?
        process.env.REACT_APP_STAGE_AUTHORIZE_PAYMENTGATEWAY_ID :
        process.env.REACT_APP_PROD_AUTHORIZE_PAYMENTGATEWAY_ID;
    const googlePayMerchantId = String(process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID);
    const appleMerchantId = getPaymode === 'test' ?
        process.env.REACT_APP_APPLE_PAY_STAGE_MERCHANT_IDENTIFIER :
        process.env.REACT_APP_APPLE_PAY_PROD_MERCHANT_IDENTIFIER;
    const stripeMerchantId = publishKey;
    const gatewayMerchantId = paymentOption === 'stripe' ? stripeMerchantId : authMerchantId || '';
    const isApplePayAvailable = getGeneralOptions && getGeneralOptions.apple_pay === true;
    const isGooglePayAvailable = getGeneralOptions && getGeneralOptions.google_pay === true;
    const isSamePreviousOrder = Boolean(pendingOrderData &&
        (pendingOrderData?.line_items?.length === Object.keys(cart?.items)?.length) &&
        Number(pendingOrderData?.total) === Number(totalPay));
    const isAlreadyPaymentDone = Boolean(((googlePayResponse && googlePayResponse.transaction_id) ||
        (applePayResponse && applePayResponse.transactionId))) && isSamePreviousOrder;

    useEffect(() => {
        const checkApplePay = async () => {
            if ((window as any).ApplePaySession && isApplePayAvailable) {
                setCanMakePayments(true);
            }
        };
        checkApplePay();
    }, [getGeneralOptions, isApplePayAvailable]);

    useEffect(() => {
        if (canMakePayments) {
            if (isSafariMac()) {
                const canUseApplePay = (window as any).ApplePaySession && ApplePaySession.canMakePayments();
                setIsApplePayReady(!!canUseApplePay);
            } else if (isFormValid) {
                setIsApplePayReady(true);
            } else {
                setIsApplePayReady(false);
            }
        }
    }, [canMakePayments, isFormValid, isAddressVerified])

    useEffect(() => {
        setErrorMsg('');
        if (availableDelivery) {
            setIsFormValid(false);
        } else {
            const validationStatus = validate(values);
            const isFormValid = Object.keys(validationStatus).length === 0;
            setIsFormValid(isFormValid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, availableDelivery]);

    const isSafariMac = () => {
        const ua = navigator.userAgent;
        const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        return isSafari && isMac;
    };

    const validate = (values: FormValues) => {
        const shippingMethodID = values?.shipping_method;
        const shippingMethodInputs = document.getElementsByName('shipping-method');

        const isUPSOrFreeShipping = (title: string) =>
            title === 'UPS Shipping' || title === 'UPS Free Shipping' || title === 'Free Shipping';

        if (shippingMethodInputs.length > 0) {
            const firstShippingMethodValue = (shippingMethodInputs[0] as HTMLInputElement)?.value;

            if (firstShippingMethodValue && shippingMethodID !== Number(firstShippingMethodValue)) {
                const shippingMethodByDate = ShippingMethodObj.getById(shippingMethods, Number(firstShippingMethodValue));
                return validateForm(creditCardProcessor, values, token, deliveryInfo, cart, products, isUPSOrFreeShipping(shippingMethodByDate?.data.title), totalValue, false, true);
            }
        }

        const shippingMethodByDate = ShippingMethodObj.getById(shippingMethods, Number(shippingMethodID));
        return validateForm(creditCardProcessor, values, token, deliveryInfo, cart, products, isUPSOrFreeShipping(shippingMethodByDate?.data.title), totalValue, false, true);
    }

    const handleGooglePayClick = async () => {
        if (!isFormValid) {
            setErrorMsg("Ensure all fields are completed correctly.");
            return;
        }
    };

    const handleValidation = async () => {
        if (!isFormValid) {
            setErrorMsg("Ensure all fields are completed correctly.");
            return;
        }
    }

    const handleApplePayPayment = async () => {
        if (!(window as any).ApplePaySession) {
            alert('Apple Pay is not available on this device.');
            return;
        }

        if (applePayResponse && applePayResponse.transactionId && isSamePreviousOrder) {
            const transactionId = applePayResponse.transactionId;
            oneTouchPaymentOrder(values, 'Apple Pay', transactionId);
            return;
        }

        if (creditCardProcessor === 'authorize.net') {
            const paymentRequest = {
                countryCode: 'US',
                currencyCode: 'USD',
                total: {
                    label: `MightyMeals ${getPaymode === 'test' ? '(SANDBOX)' : ''}`,
                    amount: totalPay.toString(),
                },
                supportedNetworks: ['visa', 'masterCard', 'amex'],
                merchantCapabilities: ['supports3DS'],
                // requiredBillingContactFields: ['postalAddress', 'name'],
                // requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email'],
                version: 3,
                merchantIdentifier: String(appleMerchantId),
            };

            const session = new (window as any).ApplePaySession(3, paymentRequest);
            const validationURLStaging = "https://apple-pay-gateway-cert.apple.com/paymentservices/startSession";

            session.onvalidatemerchant = async (event: { validationURL: string }) => {
                const validateURL = getPaymode === 'test' ? validationURLStaging : event.validationURL;
                try {
                    const merchantSession = await AuthNetAPI.applepayvalidatemerchant(validateURL);
                    session.completeMerchantValidation(merchantSession);
                } catch (error: any) {
                    setErrorMsg('Something went wrong. Please try again later.');
                    console.log('Merchant validation failed: ' + error.message);
                }
            };

            session.onpaymentauthorized = async (event: {
                payment: {
                    shippingContact: any;
                    billingContact: any;
                    token: { paymentData: any; };
                };
            }) => {
                const paymentData = event.payment.token.paymentData;

                try {
                    const billingContactObj = {
                        firstName: values.billing_first_name,
                        lastName: values.billing_last_name,
                        email: values.email,
                        phone: values.phone,
                        address: values.billing_address_1,
                        city: values.billing_city,
                        state: values.billing_state,
                        zipCode: values.billing_postcode,
                        country: 'US'
                    };

                    const shippingContactObj = {
                        firstName: values.use_shipping ? values.shipping_first_name : values.billing_first_name,
                        lastName: values.use_shipping ? values.shipping_last_name : values.billing_last_name,
                        address: values.use_shipping ? values.shipping_address_1 : values.billing_address_1,
                        city: values.use_shipping ? values.shipping_city : values.billing_city,
                        state: values.use_shipping ? values.shipping_state : values.billing_state,
                        zipCode: values.use_shipping ? values.shipping_postcode : values.billing_postcode,
                        country: 'US'
                    };

                    const paymentRequest = {
                        dataDescriptor: "COMMON.APPLE.INAPP.PAYMENT",
                        dataValue: btoa(JSON.stringify(paymentData))
                    };

                    AuthNetAPI.applepayProcessPayment(
                        paymentRequest,
                        billingContactObj,
                        shippingContactObj,
                        totalPay,
                        ipAddress || "",
                    ).then((response: any) => {
                        if (response.status === "success") {
                            const transactionId = response.transactionId;
                            dispatch(setApplePayPayment(response));
                            session.completePayment(ApplePaySession.STATUS_SUCCESS);
                            oneTouchPaymentOrder(values, 'Apple Pay', transactionId);
                        } else {
                            session.completePayment(ApplePaySession.STATUS_FAILURE);
                            setErrorMsg('Payment processing failed please try again later.');
                            console.log('Payment processing failed: ' + response.message);
                        }
                    });
                } catch (error) {
                    console.error("Payment processing error:", error);
                    session.completePayment(ApplePaySession.STATUS_FAILURE);
                }
            };

            session.begin();
        } else if (creditCardProcessor === 'stripe') {
            if (!stripe || !elements) {
                alert("Stripe or Elements not loaded yet.");
                return;
            }

            const paymentRequest = stripe.paymentRequest({
                country: 'US',
                currency: 'usd',
                total: {
                    label: `MightyMeals ${getPaymode === 'test' ? '(SANDBOX)' : ''}`,
                    amount: Number(totalPay) * 100,
                },
                requestPayerName: true,
                requestPayerEmail: true,
                requestPayerPhone: true,
            });

            const canMakePayment = await paymentRequest.canMakePayment();

            if (canMakePayment && canMakePayment.applePay) {
                paymentRequest.on('paymentmethod', async (event) => {
                    try {
                        const paymentMethodId = event.paymentMethod.id;
                        if (!paymentMethodId) {
                            alert("Error: Missing Payment Method ID.");
                            event.complete('fail');
                            return;
                        }

                        const response = await AuthNetAPI.applepayStripePayment(paymentMethodId, Number(totalPay));

                        if (response.status === 'requires_action') {
                            const { error, paymentIntent } = await stripe.confirmCardPayment(response.clientSecret);

                            if (error) {
                                alert("Error during payment confirmation: " + JSON.stringify(error));
                                event.complete('fail');
                                return;
                            }

                            if (paymentIntent.status === 'succeeded') {
                                event.complete('success');
                                oneTouchPaymentOrder(values, 'Apple Pay', paymentIntent.id);
                            } else {
                                event.complete('fail');
                                alert("Payment failed. Please try again.");
                            }
                        } else if (response.status === 'success') {
                            event.complete('success');
                            oneTouchPaymentOrder(values, 'Apple Pay', response.paymentIntentId);
                        } else {
                            event.complete('fail');
                            alert("Error: Payment failed.");
                        }
                    } catch (error) {
                        alert("Error in payment process: " + error);
                        event.complete('fail');
                    }
                });

                paymentRequest.show();
            } else {
                alert("Apple Pay is not available.");
            }
        }
    };

    const handlePaymentAuthorized = async (paymentData: any) => {
        if (googlePayResponse && googlePayResponse.transaction_id && isSamePreviousOrder) {
            const transactionId = googlePayResponse.transaction_id;
            oneTouchPaymentOrder(values, 'Google Pay', transactionId);
            return { transactionState: 'SUCCESS' } as google.payments.api.PaymentAuthorizationResult;
        }

        const paymentToken = paymentData.paymentMethodData.tokenizationData.token;
        const tokenData = JSON.parse(paymentToken);
        const billingContactObj = {
            firstName: values.billing_first_name,
            lastName: values.billing_last_name,
            email: values.email,
            phone: values.phone,
            address: values.billing_address_1,
            city: values.billing_city,
            state: values.billing_state,
            zipCode: values.billing_postcode,
            country: 'US'
        };

        const shippingContactObj = {
            firstName: values.use_shipping ? values.shipping_first_name : values.billing_first_name,
            lastName: values.use_shipping ? values.shipping_last_name : values.billing_last_name,
            address: values.use_shipping ? values.shipping_address_1 : values.billing_address_1,
            city: values.use_shipping ? values.shipping_city : values.billing_city,
            state: values.use_shipping ? values.shipping_state : values.billing_state,
            zipCode: values.use_shipping ? values.shipping_postcode : values.billing_postcode,
            country: 'US'
        };
        const payment_Data = {
            amount: Number(totalPay),
            token: tokenData,
            paymentProcessor: creditCardProcessor,
            billing: billingContactObj,
            shipping: shippingContactObj,
            ipAddress: ipAddress || "",
        }

        const response = await GooglePayAPI.processPayment(payment_Data);
        const transactionSuccessful = response && response?.messages &&
            response?.messages?.resultCode === 'Success' &&
            response?.transaction_id && Number(response?.transaction_id) !== 0;

        if (transactionSuccessful) {
            dispatch(setGooglePayPayment(response));
            oneTouchPaymentOrder(values, 'Google Pay', response.transaction_id);
            return { transactionState: 'SUCCESS' } as google.payments.api.PaymentAuthorizationResult;
        } else {
            console.error('Payment Failed:', response);
            setErrorMsg('The transaction was unsuccessful.');
            setTimeout(() => {
                setErrorMsg('');
            }, 5000);
            return { transactionState: 'ERROR' } as google.payments.api.PaymentAuthorizationResult;
        }
    };

    const handleError = (error: any) => {
        console.log(error, "error")
        setErrorMsg('Uh oh, something went wrong. Please try again later.');
        setTimeout(() => {
            setErrorMsg('');
        }, 5000);
    };

    const handlePendingOrder = () => {
        if (googlePayResponse && googlePayResponse.transaction_id) {
            oneTouchPaymentOrder(values, 'Google Pay', googlePayResponse.transaction_id);
        } else if (applePayResponse && applePayResponse.transactionId) {
            oneTouchPaymentOrder(values, 'Apple Pay', applePayResponse.transactionId);
        }
    }

    const addCouponToOrder = (order: OrderObj, shippingCost: number) => {
        if (!cartCoupon) return;

        if (cartCoupon.isPointsCoupon(customer)) {
            order.addPointsToMeta(
                maxUsablePoints,
                customer.data.mightypoints_discount_code,
                MPConversionRate
            );
        } else if (cartCoupon.data.discount_type === 'smart_coupon') {
            order.addSmartCouponToMeta(
                cartCoupon,
                cart.getTotal(products, shippingCost, null, customer) as number);
        }
        order.data.coupon_lines = [{ "code": cartCoupon.data.code }]

    }

    const generateBrowserId = (): number => {
        const userAgent = navigator.userAgent;
        const language = navigator.language || (navigator as any).userLanguage || '';

        const fingerprint = `${userAgent} ${language}`;
        const hashedBrowserId = hashString(fingerprint);

        return hashedBrowserId;
    };

    const hashString = (data: string): number => {
        let hash = 0;

        if (data.length === 0) {
            return hash;
        }

        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }

        return hash;
    };

    const getParameterByName = (name: string, url?: string): string | null => {
        try {
            const targetUrl = url || window.location.href;

            // eslint-disable-next-line no-useless-escape
            const safeName = name.replace(/[\[\]]/g, "\\$&");

            const regex = new RegExp("[?&]" + safeName + "(=([^&#]*)|&|#|$)");
            const results = regex.exec(targetUrl);

            if (!results) return null;
            if (!results[2]) return '';

            return decodeURIComponent(results[2].replace(/\+/g, " "));
        } catch (err) {
            console.log('getParameterByName error:', err);
            return null;
        }
    };

    const createOrderFromFormValues = (values: FormValues, customerId: number, PaymentMethodTitle: any) => {
        const paymentTitle = PaymentMethodTitle && `${paymentOption}-${PaymentMethodTitle === "Google Pay" ? "gpay" : "applepay"}`
        let deliveryDate = undefined;

        if (!values.shipping_method) return null;

        const shippingMethod = ShippingMethodObj.getById(
            shippingMethods,
            values.shipping_method
        );

        if (!shippingMethod) return null;

        if (!shippingMethod.isEmailDelivery() && !(
            cart.hasProductWithCategory('gift-card', products) ||
            cart.hasProductWithCategory('mighty-bucks-gift-card', products)
        )) {
            if (!values.delivery_date) return null;

            const parsedDate = values.delivery_date instanceof Date ? values.delivery_date : new Date(values.delivery_date);

            deliveryDate = parsedDate.getFullYear().toString() + '-' +
                ('0' + (parsedDate.getMonth() + 1)).slice(-2) + '-' +
                ('0' + parsedDate.getDate()).slice(-2);
        }

        const shippingCost =
            (isUtmShipping && Number(shippingMethod.data.cost) !== 0 && shippingMethod.data.ups_delivery_method === true) ?
                utmShippingValue : shippingMethod.data.cost;

        let data = {
            "customer_id": customerId,
            "billing": {
                "first_name": values.billing_first_name,
                "last_name": values.billing_last_name,
                "address_1": values.billing_address_1,
                "address_2": values.billing_address_2,
                "city": values.billing_city,
                "country": "US",
                "state": values.billing_state,
                "postcode": values.billing_postcode,
                "email": values.email,
                "phone": values.phone
            },
            "shipping": {},
            "line_items": cart.getLineItems(products, cartCoupon),
            "coupon_lines": [],
            "shipping_lines": [{
                "method_id": 'flat_rate',
                "method_title": shippingMethod.data.title,
                "total": shippingCost
            }],
            "customer_note": values.delivery_notes,
            "status": 'processing',
            "payment_method": PaymentMethodTitle ? paymentTitle : creditCardProcessor,
            "payment_method_title": PaymentMethodTitle ? `${PaymentMethodTitle} (${paymentTitle})` : OrderObj.getPaymentMethodTitle(creditCardProcessor),
            "meta_data": [{
                "key": "Chosen Pickup Location",
                "value": shippingMethod.data.title.includes('UPS') ? 'UPS' : "I'm having my meals delivered"
            }, {
                'key': '_mm_rest_order',
                'value': "true"
            }],
        }

        if (deliveryDate) {
            data.meta_data.push({
                "key": "_delivery_date",
                "value": deliveryDate
            });
        }

        if (AppURL && AppURL !== '') {
            data.meta_data.push({
                "key": "_app_user",
                "value": "true"
            },
            );
        }

        if (!customer.hasMembership()) {
            data.meta_data.push({
                "key": "_wc_points",
                "value": cart.getPoints(products).toString()
            });
        }

        if (utmParamsURL) {
            data.meta_data.push({
                "key": "order_url_parameters",
                "value": utmParamsURL
            });
        }

        if (values.use_shipping) {
            data.shipping = {
                "first_name": values.shipping_first_name,
                "last_name": values.shipping_last_name,
                "address_1": values.shipping_address_1,
                "address_2": values.shipping_address_2,
                "city": values.shipping_city,
                "country": "US",
                "state": values.shipping_state,
                "postcode": values.shipping_postcode
            }
        } else {
            data.shipping = {
                "first_name": values.billing_first_name,
                "last_name": values.billing_last_name,
                "address_1": values.billing_address_1,
                "address_2": values.billing_address_2,
                "city": values.billing_city,
                "country": "US",
                "state": values.billing_state,
                "postcode": values.billing_postcode
            }
        }

        let order = new OrderObj(data);

        if (cartCoupon) {
            addCouponToOrder(order, parseFloat(shippingCost));
        }

        if (cart.hasProductWithCategory('gift-card', products) || cart.hasProductWithCategory('mighty-bucks-gift-card', products)) {
            if (values.send_gift_card) {
                order.addGiftCardMeta(values.gift_card_email,
                    values.gift_card_msg, true);
            } else {
                order.addGiftCardMeta(values.email);
            }
        }
        return order;
    }

    const oneTouchPaymentOrder = async (values: FormValues, PaymentMethodTitle: any, transactionId?: any) => {
        let checkoutCtxt: Record<string, any> = {
            order: new OrderObj({}),
            token: token,
            customerId: 'id' in customer.data ? customer.data.id : null,
            authNonce: '',
            affiliateData: affiliateData,
            referralVisitData: referralVisitData
        }

        setErrorMsg('');
        dispatch(setOrderInProgress(true));
        Promise.resolve().then(async () => {
            let order = createOrderFromFormValues(values, checkoutCtxt.customerId, PaymentMethodTitle);

            if (!order) {
                setErrorMsg("An error has occurred while creating an order. Please try again later");
                throw new Error("An error has occurred. Please try again later")
            };

            TiktokPixel.track(
                'PlaceAnOrder',
                {
                    content_id: cart.getProductIDs(),
                    content_name: cart.getProductNames(products),
                    quantity: cart.getItemCount(),
                    content_type: 'product',
                    value: parseFloat(checkoutCtxt.order.data.total),
                    currency: 'USD'
                }
            );

            return OrdersAPI.createOrder(checkoutCtxt.token, order.data);
        }).then(async (orderData) => {
            console.log("after create", orderData);
            if ('code' in orderData && 'message' in orderData) {
                let errorMessage = orderData.message;

                const isHtml = /<\/?[a-z][\s\S]*>/i.test(errorMessage);

                if (isHtml) {
                    const tempDiv = document.createElement("div");
                    tempDiv.innerHTML = errorMessage;
                    errorMessage = tempDiv.textContent || tempDiv.innerText;
                }
                setErrorMsg(errorMessage)
                if (orderData.id) {
                    OrdersAPI.createOrderNote(checkoutCtxt.token, orderData.id, orderData.message);
                }
                throw new Error(errorMessage);
            }

            if (!('id' in orderData)) {
                setErrorMsg('An error has occurred while creating an order id. Please try again later.')
                throw new Error('An error has occurred. Please try again later.');
            }

            // Deep copy order and add to context
            checkoutCtxt.order = await new OrderObj(JSON.parse(JSON.stringify(orderData)));

            const paymentProcessor = (paymentOption === 'stripe') ? "Stripe" : "Authorize.net";
            if (transactionId) {
                if (orderData) {
                    dispatch(addOrder(orderData));
                }
                if ('authNetTransId' in checkoutCtxt) {
                    // Make deep copy of the meta_data
                    orderData.meta_data = JSON.parse(JSON.stringify(checkoutCtxt.order.data.meta_data));
                    orderData.meta_data.push({
                        "key": "_authorize_net_trans_id",
                        "value": checkoutCtxt.authNetTransId
                    });
                }
                return OrdersAPI.createOrderNote(checkoutCtxt.token, checkoutCtxt.order.data.id, `${paymentProcessor} charge complete (ID: ${transactionId})`);
            } else {
                const orderStatus = 'pending';
                let orderData: Record<string, any> = { status: orderStatus };
                OrdersAPI.createOrderNote(checkoutCtxt.token, checkoutCtxt.order.data.id, `Transaction id not found in the ${paymentProcessor} payment.`);
                await OrdersAPI.updateOrder(checkoutCtxt.token, checkoutCtxt.order.data.id, orderData);
                throw new Error('Something went wrong, please try again later.');
            }
        }).then(async () => {
            const browserId = generateBrowserId();
            const userClickID = getParameterByName('fbclid');

            // Order is complete. Track purchase with FB Pixel and GA4
            ReactPixel.track(
                'Purchase',
                {
                    currency: 'USD',
                    num_items: cart.getItemCount(),
                    content_type: 'product',
                    content_ids: cart.getProductIDs(),
                    content_name: cart.getProductNames(products),
                    value: checkoutCtxt.order.data.total,
                    click_id: userClickID,
                    browser_id: browserId,
                    ip_address: ipAddress,
                }
            );
            ReactGA.event(
                'purchase',
                {
                    currency: 'USD',
                    transaction_id: checkoutCtxt.order.data.id,
                    value: checkoutCtxt.order.data.total,
                    items: cart.getItemsForGA(products)
                }
            );
            TiktokPixel.track(
                'CompletePayment',
                {
                    content_id: cart.getProductIDs(),
                    content_name: cart.getProductNames(products),
                    quantity: cart.getItemCount(),
                    content_type: 'product',
                    value: parseFloat(checkoutCtxt.order.data.total),
                    currency: 'USD'
                }
            );

            // Reddit Pixel tracking
            const email = customer.data.email ?
                customer.data.email : checkoutCtxt.order.data.billing.email ?
                    checkoutCtxt.order.data.billing.email : '';
            const items = await Object.values(cart.items).map((item: any) => {
                const product = ProductObj.getById(products, item.product_id);
                return {
                    id: product?.data.id,
                    name: product?.data.name,
                    category: product?.data.categories,
                }
            });
            (window as any).rdt('track', 'Purchase', {
                email: email,
                currency: 'USD',
                itemCount: cart.getItemCount(),
                transactionId: checkoutCtxt.order.data.id,
                value: checkoutCtxt.order.data.total,
                products: items
            });

            CheckoutUtils.maybePostReferral(customer, checkoutCtxt, lifeTimeAffiliate, affiliateVisitorID);
            dispatch(setPendingOrder({}));
            dispatch(clearCart(checkoutCtxt.token));
            dispatch(resetCustomer());
            dispatch(setPointsCoupon(null));
            dispatch(resetReferrals());
            dispatch(setApplePayPayment(null));
            dispatch(setGooglePayPayment(null));

            navigate(`/checkout/order-received/${checkoutCtxt.order.data.id}${urlParams}`);
        }).catch((e) => {
            setErrorMsg(e.message);
        }).finally(() => {
            dispatch(setOrderInProgress(false));
        });
    }

    return (
        <>
            {isAlreadyPaymentDone ?
                <div className='d-grid gap-2'>
                    <Button
                        variant='dark'
                        className='my-3'
                        size="lg"
                        onClick={handlePendingOrder}
                        disabled={Boolean(orderInProgress)}
                    >
                        {orderInProgress ? <>
                            Placing order ...&nbsp;&nbsp;
                            <Spinner
                                animation="border"
                                as="span"
                                size="sm"
                            />
                        </> : 'Place order'}
                    </Button>
                </div> :
                <>
                    {(canMakePayments || isGooglePayAvailable) &&
                        <div className='d-flex justify-content-center pb-2 fw-bold'>- OR -</div>}
                    <Row className='text-center paymentBtn'>
                        <Col className="d-flex justify-content-center">
                            {canMakePayments && (
                                <Button
                                    className="px-3 me-3"
                                    variant="dark"
                                    disabled={orderInProgress}
                                    onClick={isApplePayReady ? handleApplePayPayment : handleValidation}
                                >
                                    <FontAwesomeIcon icon={faApple} />&nbsp;Pay
                                </Button>
                            )}
                            {isGooglePayAvailable && (!isFormValid || orderInProgress) ?
                                <Button
                                    className='px-3'
                                    disabled={orderInProgress}
                                    variant="dark"
                                    onClick={handleGooglePayClick}>
                                    <FontAwesomeIcon icon={faGoogle} />&nbsp;Pay
                                </Button> :
                                isGooglePayAvailable &&
                                <GooglePayButton
                                    environment={paymentMode}
                                    paymentRequest={{
                                        apiVersion: 2,
                                        apiVersionMinor: 0,
                                        allowedPaymentMethods: [
                                            {
                                                type: 'CARD',
                                                parameters: {
                                                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                                                    allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
                                                },
                                                tokenizationSpecification: {
                                                    type: 'PAYMENT_GATEWAY',
                                                    parameters: {
                                                        gateway: paymentGateway,
                                                        ...(paymentGateway !== "stripe" ? {
                                                            gatewayMerchantId: gatewayMerchantId || ''
                                                        } : {}),
                                                        ...(paymentGateway === "stripe" ? {
                                                            'stripe:publishableKey': `${publishKey}`,
                                                            'stripe:version': '2020-08-27'
                                                        } : {})
                                                    },
                                                },
                                            },
                                        ],
                                        merchantInfo: {
                                            merchantId: googlePayMerchantId,
                                            merchantName: 'MightyMeals',
                                        },
                                        transactionInfo: {
                                            totalPriceStatus: 'FINAL',
                                            totalPriceLabel: 'Total',
                                            totalPrice: `${totalPay}`,
                                            currencyCode: 'USD',
                                            countryCode: 'US',
                                        },
                                        shippingAddressRequired: false,
                                        callbackIntents: ['PAYMENT_AUTHORIZATION'],
                                        shippingAddressParameters: {
                                            allowedCountryCodes: ['US'],
                                        },
                                    }}
                                    onPaymentAuthorized={handlePaymentAuthorized}
                                    onError={handleError}
                                    existingPaymentMethodRequired={false}
                                    buttonType="short"
                                />}
                        </Col>
                    </Row>
                </>}
        </>
    )
}